"use client";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { Check, Lock, Key, MessageCircle } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function SecureLegacy() {
  const {
    ref: architectHeaderRef,
    isVisible: architectHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const {
    ref: tier1Ref,
    isVisible: tier1Visible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const {
    ref: tier2Ref,
    isVisible: tier2Visible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const {
    ref: tier3Ref,
    isVisible: tier3Visible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const {
    ref: noteRef,
    isVisible: noteVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const {
    ref: philosophyRef,
    isVisible: philosophyVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const {
    ref: milestonesRef,
    isVisible: milestonesVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const {
    ref: privacyRef,
    isVisible: privacyVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const pricingTiers = [{
    badge: null,
    title: "2 LIFE PILLARS",
    subtitle: "(5 strategic prompt questions per pillar)",
    price: "$665",
    standardPrice: "$950",
    savings: "Save $285",
    spotsRemaining: "2",
    features: ["Up to 10 total strategic prompt questions", "Up to 10 high-resolution heritage images", "Up to 2 hours of guided dialogue (conducted via Google Meet or Zoom)", "Up to 10 hours of narrative synthesis, curation & design", "A 38 page, crafted digital Heritage Compendium pdf, formatted for a 12x10 luxury coffee table book & for a Heritage Experience digital flipbook", "Up to 4 audio wisdom clips immersive voice recordings integrated directly into your Heritage Experience flipbook", "QR Code for your heirs globally to access your family legacy Heritage Experience"]
  }, {
    badge: "Most Requested",
    title: "4 LIFE PILLARS",
    subtitle: "(6 strategic prompt questions per pillar)",
    price: "$1,595",
    standardPrice: "$2,280",
    savings: "Save $685",
    spotsRemaining: "2",
    features: ["Up to 24 total strategic prompt questions", "Up to 24 high-resolution heritage images", "Up to 5 hours of guided dialogue (conducted via Google Meet or Zoom)", "Up to 24 hours of narrative synthesis, curation & design", "A 74 page, crafted digital Heritage Compendium pdf, formatted for a 12x10 luxury coffee table book & for a Heritage Experience digital flipbook", "Up to 12 audio wisdom clips immersive voice recordings integrated directly into your Heritage Experience flipbook", "QR Code for your heirs globally to access your family legacy Heritage Experience"]
  }, {
    badge: "Best Value",
    title: "6 LIFE PILLARS",
    subtitle: "(7 strategic prompt questions per pillar)",
    price: "$2,795",
    standardPrice: "$3,990",
    savings: "$1,195",
    spotsRemaining: "2",
    features: ["Up to 42 total strategic prompt questions", "Up to 42 high-resolution heritage images", "Up to 9 hours of guided dialogue (conducted via Google Meet or Zoom)", "Up to 42 hours of narrative synthesis, curation & design", "A 118 page, crafted digital Heritage Compendium pdf, formatted for a 12x10 luxury coffee table book & for a Heritage Experience digital flipbook", "Up to 24 audio wisdom clips immersive voice recordings integrated directly into your Heritage Experience flipbook", "QR Code for your heirs globally to access your family legacy Heritage Experience"]
  }];
  const milestones = [{
    number: "1",
    text: "Milestone 1 (25%): After your onboarding strategy call."
  }, {
    number: "2",
    text: "Milestone 2 (25%): After your first guided online dialogue session."
  }, {
    number: "3",
    text: "Milestone 3 (25%): Upon completion of the editorial draft of the Heritage Compendium"
  }, {
    number: "4",
    text: "Milestone 4 (25%): Prior to Final Heritage Experience Delivery & Family Legacy Archive Transfer."
  }];
  return <section id="secure-legacy" className="w-full bg-[hsl(210,100%,12%)] py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        
        <div ref={architectHeaderRef} className={`text-center mb-16 bg-[hsl(210,100%,12%)] py-12 px-6 transition-all duration-700 ${architectHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h1 className="font-[var(--font-cinzel)] font-bold tracking-[2px] text-[hsl(45,65%,52%)] mb-6 bg-transparent p-0 !text-[40px]">Expedite Your Family Wisdom Legacy</h1>
          <p className="font-[var(--font-libre-baskerville)] italic text-lg sm:text-xl text-white max-w-4xl mx-auto leading-relaxed">
            "A legacy is not what you leave for people, it is what you leave in
            them."
          </p>
        </div>

        
        <div ref={headerRef} className={`text-center mb-12 transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-[var(--font-cinzel)] font-bold tracking-[2px] text-[hsl(45,65%,52%)] mb-6 bg-transparent p-0 !text-[40px]">SPECIAL FOUNDING FAMILIES LAUNCH OFFER</h2>
          <p className="font-[var(--font-libre-baskerville)] italic text-base sm:text-lg text-white max-w-4xl mx-auto leading-relaxed">To celebrate the launch of my passion project, I am extending a one-time invitation to the first <strong className="text-[hsl(45,65%,52%)] font-bold">6</strong> families who commission me to architect their family wisdom legacy.</p>
        </div>

        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {pricingTiers.map((tier, i) => {
          const tierRefs = [tier1Ref, tier2Ref, tier3Ref];
          const tierVisible = [tier1Visible, tier2Visible, tier3Visible];
          return <div key={i} data-index={i} ref={tierRefs[i]} className={`transition-all duration-700 delay-${i * 100} ${tierVisible[i] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
                <Card className="bg-gold/3 border-[hsl(45,65%,52%)]/20 backdrop-blur-sm hover:shadow-[0_8px_32px_rgba(212,175,55,0.2)] transition-all duration-300 relative overflow-visible">
                  {tier.badge && <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                      <Badge variant="default" className="bg-[hsl(45,65%,52%)] text-[hsl(210,100%,12%)] font-[var(--font-montserrat)] font-semibold text-xs tracking-wider px-4 py-1 shadow-md">
                        {tier.badge}
                      </Badge>
                    </div>}

                  <CardHeader className="text-center pt-8 pb-4">
                    <CardTitle className="leading-none font-semibold font-[var(--font-cinzel)] text-[hsl(45,65%,52%)] tracking-wider mb-2 !font-bold !text-2xl">
                      {tier.title.split(/([234567])/).map((part, i) => ["2", "3", "4", "5", "6", "7"].includes(part) ? <strong key={i} className="font-bold text-[hsl(45,65%,52%)]">{part}</strong> : part)}
                    </CardTitle>
                    <CardDescription className="font-[var(--font-montserrat)] text-white/70 tracking-wide lowercase !font-bold !text-base">
                      {tier.subtitle.split(/(\d+)/).map((part, idx) => /\d+/.test(part) ? <strong key={idx} className="text-[hsl(45,65%,52%)] font-bold">{part}</strong> : part)}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="space-y-6">
                    
                    <div className="text-center">
                      <div className="text-5xl font-bold text-[hsl(45,65%,52%)] mb-2">
                        {tier.price}
                      </div>
                      <div className="text-sm text-white/60">
                        Standard:{" "}
                        <span className="line-through">
                          <strong className="text-[hsl(45,65%,52%)] font-bold">{tier.standardPrice}</strong>
                        </span>
                        {" — "}
                        <span className="text-[hsl(45,65%,52%)] font-semibold">
                          Save {tier.savings}
                        </span>
                      </div>
                    </div>

                    
                    <div className="flex justify-center">
                      <Badge variant="destructive" className="bg-red-600 text-white font-[var(--font-montserrat)] font-bold text-xs tracking-wider px-3 py-1.5">
                        ONLY <span className="text-[hsl(45,65%,52%)] font-bold">{tier.spotsRemaining}</span> SPOT
                        {tier.spotsRemaining === "1" ? "" : "S"} REMAINING
                      </Badge>
                    </div>

                    
                    <ul className="space-y-3">
                      {tier.features.map((feature, featureIndex) => <li key={featureIndex} data-index={featureIndex} className="flex items-start gap-3 text-white/90 text-sm">
                          <Check className="size-5 text-[hsl(45,65%,52%)] shrink-0 mt-0.5" />
                          <span className="font-[var(--font-libre-baskerville)] leading-relaxed">
                            {feature.split(/(\d+)/).map((part, i) => /\d+/.test(part) ? <strong key={i} className="text-[hsl(45,65%,52%)] font-bold">{part}</strong> : part)}
                          </span>
                        </li>)}
                    </ul>
                  </CardContent>

                  <CardFooter className="pt-6 flex-col">
                    <Link to="https://calendly.com/ancelfernandes/30-min-strategy-call" className="w-full">
                      <Button className="w-full bg-[hsl(45,65%,52%)] text-[hsl(210,100%,12%)] hover:bg-[hsl(45,65%,48%)] font-[var(--font-montserrat)] font-semibold tracking-wider text-base py-6 shadow-lg hover:shadow-xl transition-all duration-300">
                        CLAIM THIS SPOT →
                      </Button>
                    </Link>
                    
                    <div className="w-full pt-[15px] flex justify-center">
                      <Link to="https://wa.me/971504585183?text=I%20am%20interested%20in%20learning%20more%20about%20how%20I%20could%20preserve%20my%20family%20wisdom%20legacy">
                        <Button className="bg-transparent border border-[hsl(45,65%,52%)] text-[hsl(45,65%,52%)] hover:bg-[hsl(45,65%,52%)] hover:text-white font-[var(--font-lato)] font-bold tracking-wider text-sm px-6 py-3 transition-all duration-300 flex items-center gap-2">
                          <MessageCircle className="size-4" />
                          ENQUIRE VIA WHATSAPP
                        </Button>
                      </Link>
                    </div>
                  </CardFooter>
                </Card>
              </div>;
        })}
        </div>

        <div ref={noteRef} className={`mb-12 border-2 border-[hsl(45,65%,52%)] rounded-lg p-6 transition-all duration-700 ${noteVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="font-[var(--font-libre-baskerville)] text-white text-center leading-relaxed">
            <strong className="text-[hsl(45,65%,52%)]">NOTE</strong> In case you want to use 3, 5, 7, 8 or all 9 pillars, I could customise a bespoke solution for you, and recalculate the pricing, based on the number of strategic prompts that you want to use for each of these life pillars, If you have any clarifications, you could ask me during your free family wisdom legacy strategy call with me.
          </p>
        </div>

        
        <div ref={philosophyRef} className={`mb-12 bg-[hsl(210,100%,12%)] border-2 border-[hsl(45,65%,52%)] rounded-lg p-8 sm:p-12 transition-all duration-700 ${philosophyVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h3 className="font-[var(--font-cinzel)] font-bold text-2xl tracking-wider text-[hsl(45,65%,52%)] mb-6 text-center !font-sans sm:text-3xl">A Priceless Family Legacy Architecture</h3>
          <p className="font-[var(--font-libre-baskerville)] text-base sm:text-lg text-white leading-relaxed mb-6 text-center max-w-4xl mx-auto">The legacy of your stories, the weight of your advice, and the depth of your wisdom are truly priceless. Transforming a lifetime of memory into a curated, digital masterpiece requires a meticulous investment of time, technology, and deep editorial focus. My bespoke service handles the heavy lifting, architecting your family wisdom legacy for the generations to come.</p>
          <p className="font-[var(--font-libre-baskerville)] italic text-lg sm:text-xl text-[hsl(45,65%,52%)] leading-relaxed text-center max-w-3xl mx-auto">The question is not "How much is the price?" but "How soon do we begin?"</p>
        </div>

        
        <div ref={milestonesRef} className={`mb-12 transition-all duration-700 ${milestonesVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h3 className="font-[var(--font-cinzel)] font-bold text-2xl tracking-wider text-[hsl(45,65%,52%)] mb-8 text-center !font-sans sm:text-3xl">Payment Milestone Architecture</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {milestones.map((milestone, i) => <div key={i} data-index={i} className="flex flex-col items-center text-center bg-white/5 backdrop-blur-sm rounded-lg p-6 border border-[hsl(45,65%,52%)]/20 hover:border-[hsl(45,65%,52%)]/40 transition-all duration-300">
                <div className="w-16 h-16 rounded-full bg-[hsl(45,65%,52%)] text-[hsl(210,100%,12%)] flex items-center justify-center font-[var(--font-montserrat)] font-bold text-2xl mb-4">
                  {milestone.number}
                </div>
                <p className="font-[var(--font-libre-baskerville)] text-sm sm:text-base text-white leading-relaxed">
                  {milestone.text}
                </p>
              </div>)}
          </div>
        </div>

        
        <div ref={privacyRef} className={`mb-10 transition-all duration-700 ${privacyVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex flex-col items-center justify-center text-center max-w-4xl mx-auto">
            <Lock className="size-8 text-[hsl(45,65%,52%)] mb-4" />
            <p className="font-[var(--font-libre-baskerville)] text-sm sm:text-base text-white/90 leading-relaxed"><span className="font-bold text-[hsl(45,65%,52%)] tracking-wider">SOVEREIGNTY GUARANTEE:</span> All family data and recordings are processed in private, secure environments. Upon final delivery and transfer of your family legacy archive, all raw data is purged from our systems to ensure your absolute privacy.</p>
          </div>
        </div>

        
        <div ref={ctaRef} className={`text-center transition-all duration-700 ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Link to="https://calendly.com/ancelfernandes/30-min-strategy-call">
            <Button className="w-full sm:w-auto bg-[hsl(45,65%,52%)] text-[hsl(210,100%,12%)] hover:bg-[hsl(45,65%,48%)] font-[var(--font-montserrat)] font-bold tracking-wider text-sm sm:text-lg px-4 sm:px-10 py-3 sm:py-6 leading-tight shadow-lg hover:shadow-xl transition-all duration-300">BOOK COMPLIMENTARY CONSULTATION CALL</Button>
          </Link>
          <p className="font-[var(--font-montserrat)] text-sm text-[hsl(45,65%,52%)] text-center mt-10 mb-4 flex items-center justify-center gap-2">
            <Key className="size-4 shrink-0" />
            <span>Includes a private viewing of <span className="italic">The Heritage Experience</span> and <span className="italic">The Heritage Compendium</span>.</span>
          </p>
          
          
          <div className="flex justify-center pt-[15px]">
            <Link to="https://wa.me/971504585183?text=I%20am%20interested%20in%20learning%20more%20about%20how%20I%20could%20preserve%20my%20family%20wisdom%20legacy">
              <Button className="bg-transparent border border-[hsl(45,65%,52%)] text-[hsl(45,65%,52%)] hover:bg-[hsl(45,65%,52%)] hover:text-white font-[var(--font-lato)] font-bold tracking-wider text-sm px-6 py-3 transition-all duration-300 flex items-center gap-2">
                <MessageCircle className="size-4" />
                ENQUIRE VIA WHATSAPP
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>;
}